/**
 * Scan Agent - New Book Entry
 * 
 * Allows agents to proactively add books to the global library.
 * Supports ISBN lookup or manual entry.
 * 
 * Route: /scan-agent/new
 * 
 * ISBN Scanner Architecture (v2 - Native First):
 * - Uses native BarcodeDetector API (Chrome 83+, Edge 83+, Safari 17+)
 * - Zero UI artifacts (no library-injected elements)
 * - Instant detection (hardware-accelerated)
 * - Fallback to html5-qrcode only for legacy browsers
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  BookOpen,
  Search,
  Camera,
  X,
} from 'lucide-react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { ISBN_SCANNER_CONFIG } from '@/lib/scan-config';
import {
  lookupIsbn,
  findBookByIsbnAnyStatus,
  createScannedBook,
} from '@/services/scan';

// Check for native BarcodeDetector support with actual format validation
// Note: 'BarcodeDetector' in window can be true even when API doesn't work (e.g., iOS Safari)
const checkBarcodeDetectorSupport = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) {
    return false;
  }
  try {
    // Actually test if the API works by checking supported formats
    const formats = await BarcodeDetector.getSupportedFormats();
    const hasIsbnFormats = formats.some(f => ['ean_13', 'ean_8', 'upc_a', 'upc_e'].includes(f));
    if (!hasIsbnFormats) {
      console.log('[NewBook] BarcodeDetector exists but lacks ISBN formats:', formats);
      return false;
    }
    return true;
  } catch (e) {
    console.log('[NewBook] BarcodeDetector check failed:', e);
    return false;
  }
};

// Synchronous check for initial render (conservative - async check happens at scan time)
const hasNativeBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

// ============================================================================
// Types
// ============================================================================

type Step = 'choose' | 'isbn-scan' | 'isbn-manual' | 'manual-entry' | 'confirm';

interface BookInfo {
  isbn?: string;
  title: string;
  author?: string;
  coverImageUrl?: string;
  publisher?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ScanAgentNewBook() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  // Scanner refs - support both native and fallback
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const barcodeDetectorRef = useRef<BarcodeDetector | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanIntervalRef = useRef<number | null>(null);

  const [step, setStep] = useState<Step>('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scannerInitializing, setScannerInitializing] = useState(false);
  
  // Book info state
  const [bookInfo, setBookInfo] = useState<BookInfo>({ title: '' });
  const [manualIsbn, setManualIsbn] = useState('');
  const [existingBookWarning, setExistingBookWarning] = useState<string | null>(null);
  
  // Detected ISBN from scanner (processed via useEffect to avoid stale closures)
  const [detectedIsbn, setDetectedIsbn] = useState<string | null>(null);
  
  // Track which scanner is active (for DOM visibility)
  const [usingFallbackScanner, setUsingFallbackScanner] = useState(false);
  
  // Track if scanner was stopped to prevent double-stop errors
  const scannerStoppedRef = useRef(false);
  
  // Real-time scanning feedback
  const [scanStatus, setScanStatus] = useState<'searching' | 'detected' | 'processing'>('searching');
  const [lastDetectionTime, setLastDetectionTime] = useState<number>(0);
  const lastDetectionTimeRef = useRef<number>(0); // Ref for closure access in callbacks
  const detectionCountRef = useRef(0);

  // ============================================================================
  // Scanner Cleanup
  // ============================================================================

  const cleanupScanner = useCallback(() => {
    // Stop scan interval (native scanner)
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    // Stop native detector (no cleanup needed, just nullify)
    barcodeDetectorRef.current = null;
    
    // Stop html5-qrcode fallback - only if it's actually scanning
    if (scannerRef.current) {
      try {
        // Check if scanner is running before stopping
        if (scannerRef.current.isScanning) {
          scannerRef.current.stop().catch(() => {});
        }
      } catch {
        // Ignore errors during cleanup
      }
      scannerRef.current = null;
    }
    
    // Stop camera stream (native scanner)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setCameraActive(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupScanner();
  }, [cleanupScanner]);

  // ============================================================================
  // ISBN Scanning - Native BarcodeDetector (instant, no artifacts)
  // ============================================================================

  const startNativeScanner = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return false;

    try {
      setUsingFallbackScanner(false);
      
      // Start camera stream with HD resolution
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', 
          width: { ideal: 1920, min: 1280 }, 
          height: { ideal: 1080, min: 720 } 
        }
      });
      
      streamRef.current = stream;
      video.srcObject = stream;
      await video.play();
      
      // Apply continuous autofocus for better detection at distance
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        // Cast to any for focusMode - not in TS types but exists at runtime
        const capabilities = videoTrack.getCapabilities?.() as Record<string, unknown> | undefined;
        const settings = videoTrack.getSettings?.() as Record<string, unknown> | undefined;
        
        // Log resolution explicitly - critical for debugging detection distance
        if (settings) {
          console.log(`[NewBook] Native camera resolution: ${settings.width}x${settings.height}`);
          console.log(`[NewBook] Native camera focusMode: ${settings.focusMode || 'unknown'}`);
        }
        console.log('[NewBook] Native camera full settings:', settings);
        
        // Apply continuous autofocus if supported
        if (capabilities && 'focusMode' in capabilities) {
          try {
            await videoTrack.applyConstraints({
              advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet]
            });
            const newSettings = videoTrack.getSettings?.() as Record<string, unknown> | undefined;
            console.log(`[NewBook] Applied continuous autofocus, now: ${newSettings?.focusMode}`);
          } catch (focusErr) {
            console.log('[NewBook] Could not apply focus constraints:', focusErr);
          }
        }
      }
      
      // Create native barcode detector for ISBN formats
      const detector = new BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e']
      });
      barcodeDetectorRef.current = detector;
      
      // Scan at 30fps for instant detection with real-time feedback
      let consecutiveDetections = 0;
      let lastDetectedValue = '';
      
      scanIntervalRef.current = window.setInterval(async () => {
        if (scannerStoppedRef.current || !barcodeDetectorRef.current) return;
        
        try {
          const barcodes = await barcodeDetectorRef.current.detect(video);
          if (barcodes.length > 0) {
            const isbn = barcodes[0].rawValue;
            
            // Update detection feedback
            setLastDetectionTime(Date.now());
            setScanStatus('detected');
            
            // Require 3 consecutive identical reads for reliability
            if (isbn === lastDetectedValue) {
              consecutiveDetections++;
            } else {
              consecutiveDetections = 1;
              lastDetectedValue = isbn;
            }
            
            // Confirm after 3 consistent reads (~100ms)
            if (consecutiveDetections >= 3) {
              console.log('[NewBook] Native ISBN confirmed:', isbn);
              
              // Prevent double processing
              if (scannerStoppedRef.current) return;
              scannerStoppedRef.current = true;
              
              // Strong haptic feedback on success
              if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
              
              setScanStatus('processing');
              setDetectedIsbn(isbn);
              cleanupScanner();
            }
          } else {
            // No barcode in frame - reset to searching after 300ms
            if (Date.now() - lastDetectionTime > 300) {
              setScanStatus('searching');
              consecutiveDetections = 0;
              lastDetectedValue = '';
            }
          }
        } catch (e) {
          // Detection failed this frame - expected, continue scanning
        }
      }, 33); // ~30fps
      
      return true;
    } catch (err) {
      console.error('[NewBook] Native scanner error:', err);
      return false;
    }
  }, [cleanupScanner]);

  // ============================================================================
  // ISBN Scanning - html5-qrcode Fallback (legacy browsers)
  // ============================================================================

  // Trigger fallback scanner init AFTER React renders the visible container
  const [fallbackScannerReady, setFallbackScannerReady] = useState(false);

  const startFallbackScanner = useCallback(() => {
    // Just set state - actual init happens in useEffect after render
    setUsingFallbackScanner(true);
    setFallbackScannerReady(true);
  }, []);

  // Initialize fallback scanner after container is visible (state rendered)
  // Uses html5-qrcode video stream with HD resolution for best detection distance
  useEffect(() => {
    // Guard: only proceed if still on isbn-scan step (prevents spurious errors if user navigates away)
    if (!fallbackScannerReady || !usingFallbackScanner || step !== 'isbn-scan') return;
    
    // Reset the trigger
    setFallbackScannerReady(false);
    
    const initFallbackScanner = async () => {
      const container = document.getElementById('isbn-scanner-container');
      if (!container) {
        console.error('[NewBook] Fallback container not found after render');
        setError('Scanner initialization failed. Try manual entry.');
        setStep('choose');
        return;
      }

      try {
        const scanner = new Html5Qrcode('isbn-scanner-container', {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
          ],
          verbose: false,
          // Enable native BarcodeDetector when available for better performance
          useBarCodeDetectorIfSupported: true,
        });
        scannerRef.current = scanner;

        // html5-qrcode API:
        // - First param: simple camera selector (ONE key only)
        // - Second param: config with videoConstraints for HD resolution
        await scanner.start(
          ISBN_SCANNER_CONFIG.CAMERA_CONFIG,
          {
            fps: ISBN_SCANNER_CONFIG.FPS,
            // HD resolution via videoConstraints - dramatically improves detection distance
            // Default 640x480 requires getting very close; HD allows detection from further
            videoConstraints: ISBN_SCANNER_CONFIG.VIDEO_CONSTRAINTS,
            // NO qrbox = scan FULL frame = maximum detection area
            disableFlip: true,
          },
          (decodedText) => {
            if (scannerStoppedRef.current) return;
            
            // Validate ISBN format (10 or 13 digits only, not 12-digit UPC)
            const cleanIsbn = decodedText.replace(/[-\s]/g, '');
            const isValidIsbn = /^(\d{10}|\d{13})$/.test(cleanIsbn) || /^\d{9}X$/i.test(cleanIsbn);
            
            if (!isValidIsbn) {
              console.log('[NewBook] Ignoring non-ISBN barcode:', decodedText);
              // Show detection feedback even for invalid barcodes
              setScanStatus('detected');
              lastDetectionTimeRef.current = Date.now();
              setLastDetectionTime(lastDetectionTimeRef.current);
              return; // Keep scanning for valid ISBN
            }
            
            scannerStoppedRef.current = true;
            console.log('[NewBook] Fallback ISBN detected:', cleanIsbn);
            
            // Strong haptic feedback on success
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            setScanStatus('processing');
            
            // Stop scanner safely
            if (scannerRef.current) {
              scannerRef.current.stop().catch(() => {});
              scannerRef.current = null;
            }
            setDetectedIsbn(cleanIsbn);
          },
          () => {
            // No barcode found in this frame - reset to searching after 500ms of no detection
            // This prevents "Barcode detected - hold steady" from being stuck
            if (lastDetectionTimeRef.current > 0 && Date.now() - lastDetectionTimeRef.current > 500) {
              setScanStatus('searching');
            }
          }
        );
        
        // Log camera settings after scanner starts
        try {
          const settings = scanner.getRunningTrackSettings() as Record<string, unknown>;
          console.log(`[NewBook] Fallback camera: ${settings.width}x${settings.height}`);
        } catch {
          // Ignore if settings not available
        }
        
        console.log('[NewBook] Fallback scanner initialized (HD resolution, full frame)');
        setCameraActive(true);
      } catch (err) {
        console.error('[NewBook] Fallback scanner error:', err);
        setError('Could not start camera. Try manual entry.');
        setStep('choose');
      }
    };

    initFallbackScanner();
  }, [fallbackScannerReady, usingFallbackScanner, step]);

  // ============================================================================
  // Start Scanner (native first, fallback second)
  // ============================================================================

  useEffect(() => {
    if (step !== 'isbn-scan') return;

    let mounted = true;
    scannerStoppedRef.current = false;
    setScannerInitializing(true);
    setScanStatus('searching');
    detectionCountRef.current = 0;
    
    const initScanner = async () => {
      // Try native first (instant, no artifacts)
      // Use async check to verify API actually works (not just exists)
      const nativeSupported = await checkBarcodeDetectorSupport();
      
      if (nativeSupported && mounted) {
        const success = await startNativeScanner();
        if (success && mounted) {
          console.log('[NewBook] Using native BarcodeDetector');
          setScannerInitializing(false);
          setCameraActive(true);
          return;
        }
        console.log('[NewBook] Native scanner init failed, falling back');
      }
      
      // Fallback to html5-qrcode (via state change + useEffect)
      if (!mounted) return;
      
      console.log('[NewBook] Using html5-qrcode fallback');
      setScannerInitializing(false);
      startFallbackScanner(); // Sets state, init happens in separate useEffect
    };

    initScanner();

    return () => {
      mounted = false;
      cleanupScanner();
      setScannerInitializing(false);
    };
  }, [step, startNativeScanner, startFallbackScanner, cleanupScanner]);

  // Process detected ISBN (separate effect to avoid stale closures)
  useEffect(() => {
    if (!detectedIsbn) return;
    
    const processIsbn = async () => {
      await handleIsbnDetected(detectedIsbn);
      setDetectedIsbn(null);
    };
    
    processIsbn();
  }, [detectedIsbn]);

  const startIsbnScanner = useCallback(() => {
    setError(null);
    setStep('isbn-scan');
  }, []);

  const stopIsbnScanner = useCallback(() => {
    cleanupScanner();
    setStep('choose');
  }, [cleanupScanner]);

  // ============================================================================
  // ISBN Processing
  // ============================================================================

  const handleIsbnDetected = async (isbn: string) => {
    setLoading(true);
    setError(null);
    setExistingBookWarning(null);

    try {
      // Check if book already exists in global library (any status, not just ready)
      // This prevents duplicates when a book is still processing or needs review
      const existingBook = await findBookByIsbnAnyStatus(isbn);
      if (existingBook) {
        const statusLabel = existingBook.quality_status === 'ready'
          ? 'ready'
          : `in ${existingBook.quality_status || 'unknown'} status`;
        setExistingBookWarning(
          `This book already exists in the library (${statusLabel}): "${existingBook.title}"`
        );
        setLoading(false);
        return;
      }

      // Look up ISBN metadata
      const metadata = await lookupIsbn(isbn);
      
      if (metadata) {
        setBookInfo({
          isbn,
          title: metadata.title,
          author: metadata.author || undefined,
          coverImageUrl: metadata.coverImageUrl || undefined,
          publisher: metadata.publisher || undefined,
        });
        setStep('confirm');
      } else {
        // ISBN found but no metadata - allow manual entry
        setBookInfo({ isbn, title: '' });
        setStep('manual-entry');
        setError('ISBN found but no metadata available. Please enter details manually.');
      }
    } catch (err) {
      console.error('[NewBook] ISBN lookup error:', err);
      setError('Failed to look up ISBN. Please try manual entry.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualIsbnSubmit = async () => {
    const cleanIsbn = manualIsbn.replace(/[-\s]/g, '');
    if (!cleanIsbn || cleanIsbn.length < 10) {
      setError('Please enter a valid ISBN (10 or 13 digits)');
      return;
    }
    await handleIsbnDetected(cleanIsbn);
  };

  // ============================================================================
  // Book Creation
  // ============================================================================

  const handleCreateBook = async () => {
    if (!profile?.id) {
      setError('Not authenticated');
      return;
    }

    if (!bookInfo.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sanitizedIsbn = bookInfo.isbn?.replace(/[^0-9Xx]/g, '').toUpperCase() || undefined;
      const book = await createScannedBook(
        profile.id,
        bookInfo.title.trim(),
        {
          isbn: sanitizedIsbn,
          author: bookInfo.author,
          coverImageUrl: bookInfo.coverImageUrl,
          publisher: bookInfo.publisher,
        }
      );

      // Navigate to scan session with bookId
      navigate(`/scan-agent/scan/book/${book.id}`);
    } catch (err) {
      console.error('[NewBook] Create book error:', err);
      setError('Failed to create book');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="max-w-2xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => step === 'choose' ? navigate('/scan-agent') : setStep('choose')}
        className="flex items-center gap-2 text-[#8E8E93] hover:text-[#1C1C1E] mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {step === 'choose' ? 'Back to Queue' : 'Back'}
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#1C1C1E] mb-2">Add New Book</h1>
        <p className="text-[#8E8E93]">Add a book to the global library for all families to access.</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-xl bg-[#FF453A]/10 border border-[#FF453A]/20 text-[#FF453A] flex items-center gap-3 mb-6">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Existing Book Warning */}
      {existingBookWarning && (
        <div className="p-4 rounded-xl bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 text-[#FF9F0A] mb-6">
          <p className="mb-3">{existingBookWarning}</p>
          <button
            onClick={() => {
              setExistingBookWarning(null);
              setStep('choose');
            }}
            className="text-sm underline"
          >
            Start over
          </button>
        </div>
      )}

      {/* Step: Choose Method */}
      {step === 'choose' && !existingBookWarning && (
        <div className="space-y-4">
          <button
            onClick={startIsbnScanner}
            className="w-full p-6 rounded-2xl text-left transition-all hover:brightness-110"
            style={glassStyles.card}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#5C2D91]/15 rounded-xl flex items-center justify-center">
                <Camera className="w-6 h-6 text-[#5C2D91]" />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1C1E] mb-1">Scan ISBN Barcode</h3>
                <p className="text-sm text-[#8E8E93]">Use camera to scan the book's barcode</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setStep('isbn-manual')}
            className="w-full p-6 rounded-2xl text-left transition-all hover:brightness-110"
            style={glassStyles.card}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#5E5CE6]/15 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-[#5E5CE6]" />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1C1E] mb-1">Enter ISBN Manually</h3>
                <p className="text-sm text-[#8E8E93]">Type the ISBN to look up book details</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setStep('manual-entry')}
            className="w-full p-6 rounded-2xl text-left transition-all hover:brightness-110"
            style={glassStyles.card}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#30D158]/15 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#30D158]" />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1C1E] mb-1">Manual Entry</h3>
                <p className="text-sm text-[#8E8E93]">Enter book details without ISBN lookup</p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Step: ISBN Scanner */}
      {step === 'isbn-scan' && (
        <div className="space-y-4">
          {/* Compact scanner window - square aspect ratio signals "get close" */}
          <div className="relative rounded-2xl bg-black overflow-hidden mx-auto max-w-xs" style={glassStyles.card}>
            {/* Native Video Element (used by BarcodeDetector) */}
            <video
              ref={videoRef}
              className={`w-full aspect-square object-cover ${usingFallbackScanner ? 'hidden' : ''}`}
              playsInline
              muted
              autoPlay
            />
            
            {/* html5-qrcode fallback container - always in DOM for runtime fallback */}
            <div 
              id="isbn-scanner-container" 
              className={`w-full aspect-square [&_video]:!object-cover [&>div]:!bg-transparent ${usingFallbackScanner ? '' : 'hidden'}`}
            />
            
            {/* Initializing Overlay */}
            {scannerInitializing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-[#5C2D91] animate-spin" />
                  <span className="text-white text-sm">Starting camera...</span>
                </div>
              </div>
            )}
            
            {/* Scan guide with real-time feedback */}
            {cameraActive && !scannerInitializing && (
              <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
                <div className="w-72 h-36 relative">
                  {/* Corner brackets - color changes based on detection status */}
                  <div className={`absolute -top-0.5 -left-0.5 w-8 h-8 border-t-2 border-l-2 rounded-tl-lg transition-colors duration-150 ${
                    scanStatus === 'processing' ? 'border-[#30D158]' :
                    scanStatus === 'detected' ? 'border-[#FF9F0A]' :
                    'border-[#5C2D91]'
                  }`} />
                  <div className={`absolute -top-0.5 -right-0.5 w-8 h-8 border-t-2 border-r-2 rounded-tr-lg transition-colors duration-150 ${
                    scanStatus === 'processing' ? 'border-[#30D158]' :
                    scanStatus === 'detected' ? 'border-[#FF9F0A]' :
                    'border-[#5C2D91]'
                  }`} />
                  <div className={`absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-2 border-l-2 rounded-bl-lg transition-colors duration-150 ${
                    scanStatus === 'processing' ? 'border-[#30D158]' :
                    scanStatus === 'detected' ? 'border-[#FF9F0A]' :
                    'border-[#5C2D91]'
                  }`} />
                  <div className={`absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-2 border-r-2 rounded-br-lg transition-colors duration-150 ${
                    scanStatus === 'processing' ? 'border-[#30D158]' :
                    scanStatus === 'detected' ? 'border-[#FF9F0A]' :
                    'border-[#5C2D91]'
                  }`} />
                  
                  {/* Scanning line animation - only when searching */}
                  {scanStatus === 'searching' && (
                    <div className="absolute inset-x-0 top-0 h-full overflow-hidden">
                      <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-[#5C2D91] to-transparent animate-scan-line" />
                    </div>
                  )}
                  
                  {/* Detection pulse - when barcode detected */}
                  {scanStatus === 'detected' && (
                    <div className="absolute inset-0 border-2 border-[#FF9F0A] rounded-lg animate-pulse" />
                  )}
                  
                  {/* Success checkmark - when processing */}
                  {scanStatus === 'processing' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-[#30D158] rounded-full flex items-center justify-center animate-scale-in">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Status Text - real-time feedback */}
          <div className="text-center">
            {scannerInitializing && (
              <p className="text-sm text-[#8E8E93]">Requesting camera access...</p>
            )}
            {cameraActive && !scannerInitializing && scanStatus === 'searching' && (
              <div className="space-y-1">
                <p className="text-[#1C1C1E] font-medium flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-[#5C2D91] rounded-full animate-pulse" />
                  Looking for barcode...
                </p>
                <p className="text-sm text-[#8E8E93]">
                  Hold camera 4-6 inches from barcode
                </p>
                {usingFallbackScanner && (
                  <p className="text-xs text-[#AEAEB2] mt-1">
                    Tip: Move closer if not detecting
                  </p>
                )}
              </div>
            )}
            {cameraActive && !scannerInitializing && scanStatus === 'detected' && (
              <div className="space-y-1">
                <p className="text-[#FF9F0A] font-medium flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-[#FF9F0A] rounded-full" />
                  Barcode detected - hold steady
                </p>
                <p className="text-sm text-[#8E8E93]">
                  Keep the camera still for a moment
                </p>
              </div>
            )}
            {cameraActive && !scannerInitializing && scanStatus === 'processing' && (
              <div className="space-y-1">
                <p className="text-[#30D158] font-medium flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  ISBN captured! Looking up book...
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={stopIsbnScanner}
            className="w-full py-3 rounded-xl bg-black/[0.05] text-[#636366] hover:bg-black/[0.08] transition-all"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Step: Manual ISBN Entry */}
      {step === 'isbn-manual' && (
        <div className="space-y-4">
          <div className="p-6 rounded-2xl" style={glassStyles.card}>
            <label className="block text-sm font-medium text-[#636366] mb-2">
              ISBN (10 or 13 digits)
            </label>
            <input
              type="text"
              value={manualIsbn}
              onChange={(e) => setManualIsbn(e.target.value)}
              placeholder="978-0-123456-78-9"
              className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:border-[#5C2D91]/50 focus:outline-none"
              autoFocus
            />
          </div>
          <button
            onClick={handleManualIsbnSubmit}
            disabled={loading || !manualIsbn.trim()}
            className="w-full py-4 rounded-xl bg-[#5C2D91] text-white font-medium transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Looking up...
              </>
            ) : (
              'Look Up ISBN'
            )}
          </button>
        </div>
      )}

      {/* Step: Manual Entry */}
      {step === 'manual-entry' && (
        <div className="space-y-4">
          <div className="p-6 rounded-2xl space-y-4" style={glassStyles.card}>
            <div>
              <label className="block text-sm font-medium text-[#636366] mb-2">
                Title <span className="text-[#FF453A]">*</span>
              </label>
              <input
                type="text"
                value={bookInfo.title}
                onChange={(e) => setBookInfo(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Book title"
                className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:border-[#5C2D91]/50 focus:outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#636366] mb-2">
                Author
              </label>
              <input
                type="text"
                value={bookInfo.author || ''}
                onChange={(e) => setBookInfo(prev => ({ ...prev, author: e.target.value }))}
                placeholder="Author name"
                className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:border-[#5C2D91]/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#636366] mb-2">
                ISBN (optional)
              </label>
              <input
                type="text"
                value={bookInfo.isbn || ''}
                onChange={(e) => setBookInfo(prev => ({ ...prev, isbn: e.target.value }))}
                placeholder="978-0-123456-78-9"
                className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:border-[#5C2D91]/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#636366] mb-2">
                Publisher (optional)
              </label>
              <input
                type="text"
                value={bookInfo.publisher || ''}
                onChange={(e) => setBookInfo(prev => ({ ...prev, publisher: e.target.value }))}
                placeholder="Publisher name"
                className="w-full px-4 py-3 rounded-xl bg-black/[0.03] border border-black/[0.08] text-[#1C1C1E] placeholder-[#AEAEB2] focus:border-[#5C2D91]/50 focus:outline-none"
              />
            </div>
          </div>
          <button
            onClick={() => setStep('confirm')}
            disabled={!bookInfo.title.trim()}
            className="w-full py-4 rounded-xl bg-[#5C2D91] text-white font-medium transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      )}

      {/* Step: Confirm */}
      {step === 'confirm' && (
        <div className="space-y-6">
          <div className="p-6 rounded-2xl" style={glassStyles.card}>
            <div className="flex items-start gap-4">
              {/* Cover */}
              <div className="w-24 h-32 bg-black/[0.03] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {bookInfo.coverImageUrl ? (
                  <img 
                    src={bookInfo.coverImageUrl} 
                    alt={bookInfo.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <BookOpen className="w-8 h-8 text-[#AEAEB2]" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-[#1C1C1E] mb-1">{bookInfo.title}</h2>
                {bookInfo.author && (
                  <p className="text-[#8E8E93] mb-2">{bookInfo.author}</p>
                )}
                <div className="space-y-1 text-sm text-[#8E8E93]">
                  {bookInfo.isbn && <div>ISBN: {bookInfo.isbn}</div>}
                  {bookInfo.publisher && <div>Publisher: {bookInfo.publisher}</div>}
                </div>
              </div>

              {/* Edit Button */}
              <button
                onClick={() => setStep('manual-entry')}
                className="p-2 rounded-lg hover:bg-black/[0.05] text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
              >
                Edit
              </button>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[#5C2D91]/10 border border-[#5C2D91]/20">
            <p className="text-sm text-[#5C2D91]">
              After creating the book, you'll scan the pages and then edit the content 
              before publishing to the global library.
            </p>
          </div>

          <button
            onClick={handleCreateBook}
            disabled={loading}
            className="w-full py-4 rounded-xl bg-[#30D158] text-white font-medium text-lg transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Book & Start Scanning'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default ScanAgentNewBook;
